import { useEffect, useState } from "react";
import { useOutletContext, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  UserPlus, ShieldCheck, PhoneCall, Copy, MoreVertical,
  UserX, UserCheck, Crown, Mail, Clock,
} from "lucide-react";

const ROLE_META = {
  owner: { label: "Owner", icon: Crown,       color: "text-[#C9A24A]" },
  admin: { label: "Owner", icon: Crown,       color: "text-[#C9A24A]" },
  sales: { label: "Sales", icon: PhoneCall,   color: "text-blue-400" },
};

/**
 * StaffTeamPage — owner-only page to add team members and change roles.
 * On successful invite the API returns a `provisional_password` which we
 * display once (copyable) — v2 will replace with an email-invite link.
 */
export default function StaffTeamPage() {
  const { me } = useOutletContext();
  const isOwner = me.role === "owner" || me.role === "admin";
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [justInvited, setJustInvited] = useState(null); // { name, email, provisional_password }

  const load = () => {
    if (!isOwner) return;
    setLoading(true);
    api.get("/staff/team")
      .then(({ data }) => setTeam(data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isOwner]);

  if (!isOwner) return <Navigate to="/staff" replace />;

  const toggleActive = async (member) => {
    try {
      await api.patch(`/staff/team/${member.id}`, { staff_deactivated: member.active });
      toast.success(member.active ? "Member deactivated" : "Member reactivated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const changeRole = async (member, role) => {
    try {
      await api.patch(`/staff/team/${member.id}`, { role });
      toast.success(`Role updated to ${role}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="staff-team-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A24A] font-bold mb-2">Team management</p>
          <h1 className="font-display text-4xl text-white mb-1">Your team</h1>
          <p className="text-sm text-gray-500">
            Invite sales staff. They'll only see leads (not billing or moderation).
          </p>
        </div>
        <Button
          data-testid="invite-teammate-btn"
          onClick={() => setInviteOpen(true)}
          className="h-11 rounded-lg bg-gradient-to-r from-[#C9A24A] to-[#A8842E] text-[#1B0F14] font-bold hover:brightness-110"
        >
          <UserPlus size={15} className="mr-2" /> Invite teammate
        </Button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-500 text-sm">Loading team…</div>
      ) : (
        <div className="grid gap-3" data-testid="team-list">
          {team.map((m) => {
            const meta = ROLE_META[m.role] || ROLE_META.sales;
            const RoleIcon = meta.icon;
            const isSelf = m.id === me.id;
            return (
              <div
                key={m.id}
                data-testid={`team-row-${m.id}`}
                className={`bg-[#0F1420] border rounded-xl p-4 flex items-center gap-4 ${
                  m.active ? "border-white/5" : "border-red-500/20 opacity-60"
                }`}
              >
                <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#7A1E2A]/40 to-[#C9A24A]/40 flex items-center justify-center">
                  <RoleIcon size={16} className={meta.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display text-lg text-white truncate">{m.name || "—"}</p>
                    <span className={`text-[10px] uppercase tracking-widest font-bold ${meta.color}`}>{meta.label}</span>
                    {isSelf && (
                      <span className="text-[10px] uppercase tracking-widest bg-white/10 text-gray-400 px-1.5 py-0.5 rounded">You</span>
                    )}
                    {!m.active && (
                      <span className="text-[10px] uppercase tracking-widest bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded">Deactivated</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                    <span className="inline-flex items-center gap-1"><Mail size={11} /> {m.email}</span>
                    {m.phone && <span className="inline-flex items-center gap-1"><PhoneCall size={11} /> {m.phone}</span>}
                    {m.last_seen_at && (
                      <span className="inline-flex items-center gap-1 text-gray-500"><Clock size={11} /> {_relative(m.last_seen_at)}</span>
                    )}
                  </div>
                </div>
                {!isSelf && (
                  <div className="flex items-center gap-2">
                    <Select value={m.role === "admin" ? "owner" : m.role} onValueChange={(v) => changeRole(m, v)}>
                      <SelectTrigger data-testid={`role-select-${m.id}`} className="w-32 h-9 bg-[#0B0F17] border-white/10 text-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sales">Sales</SelectItem>
                        <SelectItem value="owner">Owner</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      data-testid={`toggle-active-${m.id}`}
                      onClick={() => toggleActive(m)}
                      variant="outline"
                      className={`h-9 px-3 rounded-lg text-xs ${
                        m.active
                          ? "bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10"
                          : "bg-transparent border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                      }`}
                    >
                      {m.active ? (<><UserX size={12} className="mr-1" /> Deactivate</>) : (<><UserCheck size={12} className="mr-1" /> Reactivate</>)}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-[#0F1420] border-white/10 text-white max-w-md" data-testid="invite-dialog">
          <InviteForm onClose={() => setInviteOpen(false)} onInvited={(res) => { setJustInvited(res); load(); setInviteOpen(false); }} />
        </DialogContent>
      </Dialog>

      {/* Provisional-password reveal */}
      {justInvited && (
        <Dialog open onOpenChange={() => setJustInvited(null)}>
          <DialogContent className="bg-[#0F1420] border-[#C9A24A]/40 text-white max-w-md" data-testid="password-reveal-dialog">
            <DialogHeader>
              <DialogTitle className="text-white font-display text-xl flex items-center gap-2">
                <ShieldCheck size={18} className="text-[#C9A24A]" /> Copy these credentials
              </DialogTitle>
              <p className="text-xs text-gray-400 pt-1">
                {justInvited.reused
                  ? "This user already had an account — we promoted them to staff. They keep their existing password."
                  : "Share these with the new teammate. This password is shown only once."}
              </p>
            </DialogHeader>
            {!justInvited.reused && (
              <div className="bg-[#0B0F17] border border-white/10 rounded-lg p-4 mt-2 font-mono text-sm space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-xs uppercase tracking-widest">Email</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(justInvited.email); toast.success("Copied"); }}
                    className="text-[#C9A24A] hover:text-white"
                  ><Copy size={12} /></button>
                </div>
                <p className="text-white">{justInvited.email}</p>
                <div className="border-t border-white/10 pt-2 flex items-center justify-between">
                  <span className="text-gray-500 text-xs uppercase tracking-widest">Password</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(justInvited.provisional_password); toast.success("Copied"); }}
                    className="text-[#C9A24A] hover:text-white"
                  ><Copy size={12} /></button>
                </div>
                <p className="text-white select-all">{justInvited.provisional_password}</p>
              </div>
            )}
            <Button
              onClick={() => setJustInvited(null)}
              className="w-full mt-2 bg-gradient-to-r from-[#C9A24A] to-[#A8842E] text-[#1B0F14] font-bold hover:brightness-110"
            >
              Got it
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


function InviteForm({ onClose, onInvited }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("sales");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/staff/team/invite", { name, email, phone, role });
      onInvited({ ...data, name, email });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <DialogHeader>
        <DialogTitle className="text-white font-display text-xl">Invite a teammate</DialogTitle>
        <p className="text-xs text-gray-400">
          We'll create their account. You'll get a one-time password to share.
        </p>
      </DialogHeader>
      <div>
        <label className="text-xs uppercase tracking-widest font-bold text-gray-400 mb-1 block">Full name</label>
        <Input
          data-testid="invite-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required minLength={2}
          className="bg-[#0B0F17] border-white/10 text-white h-11"
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest font-bold text-gray-400 mb-1 block">Work email</label>
        <Input
          data-testid="invite-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="bg-[#0B0F17] border-white/10 text-white h-11"
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest font-bold text-gray-400 mb-1 block">Phone <span className="text-gray-600">(optional)</span></label>
        <Input
          data-testid="invite-phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="bg-[#0B0F17] border-white/10 text-white h-11"
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest font-bold text-gray-400 mb-1 block">Role</label>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="invite-role" className="bg-[#0B0F17] border-white/10 text-white h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sales">Sales — leads only</SelectItem>
            <SelectItem value="owner">Owner — full access</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white hover:bg-white/5">Cancel</Button>
        <Button
          type="submit"
          data-testid="invite-submit"
          disabled={busy}
          className="bg-gradient-to-r from-[#C9A24A] to-[#A8842E] text-[#1B0F14] font-bold hover:brightness-110"
        >
          {busy ? "Creating…" : "Create account"}
        </Button>
      </div>
    </form>
  );
}


function _relative(iso) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
}
